import type { IconButtonProps, ButtonProps } from '@workramp/ui';
import { IconButton, Button } from '@workramp/ui';
import { AppState, I18n } from '~/common/explicit-exports';
import { ButtonWrapper } from './BookmarkButton.styles';

interface BaseBookmarkButtonProps {
  isBookmarked: boolean;
  isLoading?: boolean;
  onAdd: () => void;
  onRemove: () => void;
  buttonType?: 'icon' | 'button';
  removeLabel?: string;
  addLabel?: string;
}

interface IconBookmarkButtonProps
  extends Omit<IconButtonProps, 'label' | 'iconName' | 'onPress'>,
    BaseBookmarkButtonProps {
  buttonType?: 'icon';
}

interface RegularBookmarkButtonProps extends Omit<ButtonProps, 'onPress'>, BaseBookmarkButtonProps {
  buttonType: 'button';
}

export type BookmarkButtonProps = IconBookmarkButtonProps | RegularBookmarkButtonProps;

export function BookmarkButton({
  isBookmarked,
  isLoading = false,
  onAdd,
  onRemove,
  buttonType = 'icon',
  variant = 'text',
  color = 'secondary',
  removeLabel,
  addLabel,
  ...props
}: BookmarkButtonProps) {
  const ADD_LABEL = addLabel || I18n.t('js.learner.saved_content.add_to_saved_content');
  const REMOVE_LABEL = removeLabel || I18n.t('js.learner.saved_content.remove_from_saved_content');

  const handleBookmark = () => (isBookmarked ? onRemove() : onAdd());

  const { isWhiteLabel } = AppState;

  if (isWhiteLabel) return null;

  return (
    <ButtonWrapper $isHidden={isLoading}>
      {buttonType === 'icon' ? (
        <IconButton
          label={isBookmarked ? REMOVE_LABEL : ADD_LABEL}
          iconName={isBookmarked ? 'bookmark:solid' : 'bookmark:regular'}
          variant={variant}
          color={color}
          onPress={handleBookmark}
          {...props}
        />
      ) : (
        <Button
          variant={variant}
          color={color}
          onPress={handleBookmark}
          leadingIconName={isBookmarked ? 'bookmark:solid' : 'bookmark:regular'}
          {...props}
        >
          {isBookmarked ? REMOVE_LABEL : ADD_LABEL}
        </Button>
      )}
    </ButtonWrapper>
  );
}
